import React from 'react';
import { STORAGE_KEYS } from '../constants';
import { extractCityFromGeocoderGeoObject } from '../utils/addressUtils';

// Сервис для работы с адресами
export interface AddressSuggestion {
  /** Краткий адрес без страницы (например с бэкенд-прокси Яндекса) */
  address?: string;
  /** Полная строка геокодера (formatted), если отличается от address */
  fullAddress?: string;
  value: string;
  label: string | React.ReactNode;
  coordinates?: {
    lat: number;
    lng: number;
  };
  postalCode?: string;
  region?: string;
  city?: string;
  street?: string;
  house?: string;
}

export interface AddressService {
  searchAddresses(query: string): Promise<AddressSuggestion[]>;
  getAddressDetails(address: string): Promise<AddressSuggestion | null>;
}

// Яндекс.Карты API сервис
export class YandexAddressService implements AddressService {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async searchAddresses(query: string): Promise<AddressSuggestion[]> {
    if (!query || query.length < 3) return [];

    console.log('🌍 Яндекс.Карты API запрос:', query);
    console.log('🔑 API ключ:', this.apiKey ? this.apiKey.substring(0, 8) + '...' : 'undefined');

    try {
      const url = `https://geocode-maps.yandex.ru/1.x/?apikey=${this.apiKey}&format=json&geocode=${encodeURIComponent(query)}&lang=ru_RU&results=10`;
      console.log('🌐 URL запроса:', url);

      const response = await fetch(url);

      console.log('📡 Статус ответа:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Ошибка запроса к Яндекс.Карты API: ${response.status} ${response.statusText}`);
      }

      const data: any = await response.json();
      console.log('📦 Ответ API:', data);

      const features: any[] = data.response.GeoObjectCollection.featureMember || [];
      console.log('📍 Найдено объектов:', features.length);

      return features.map((feature: any) => {
        const geoObject: any = feature.GeoObject;
        const point: number[] = geoObject.Point.pos.split(' ').map(Number);
        const addressData: any = geoObject.metaDataProperty.GeocoderMetaData.Address;
        const cityFromComponents = extractCityFromGeocoderGeoObject(geoObject);

        return {
          value: geoObject.metaDataProperty.GeocoderMetaData.text,
          label: geoObject.metaDataProperty.GeocoderMetaData.text,
          coordinates: {
            lng: point[0],
            lat: point[1]
          },
          postalCode: addressData?.postal_code,
          region: addressData?.region,
          city: cityFromComponents ?? addressData?.locality ?? '',
          street: addressData?.street,
          house: addressData?.house
        };
      });
    } catch (error) {
      console.error('❌ Ошибка поиска адресов в Яндекс.Карты:', error);
      return [];
    }
  }

  async getAddressDetails(addressQuery: string): Promise<AddressSuggestion | null> {
    try {
      const response: Response = await fetch(
        `https://geocode-maps.yandex.ru/1.x/?apikey=${this.apiKey}&format=json&geocode=${encodeURIComponent(addressQuery)}&lang=ru_RU&results=1`
      );

      if (!response.ok) {
        throw new Error('Ошибка запроса к Яндекс.Карты API');
      }

      const data: any = await response.json();
      const features: any[] = data.response.GeoObjectCollection.featureMember || [];

      if (features.length === 0) return null;

      const feature: any = features[0];
      const geoObject: any = feature.GeoObject;
      const point: number[] = geoObject.Point.pos.split(' ').map(Number);
      const addressData: any = geoObject.metaDataProperty.GeocoderMetaData.Address;
      const cityFromComponents = extractCityFromGeocoderGeoObject(geoObject);

      return {
        value: geoObject.metaDataProperty.GeocoderMetaData.text,
        label: geoObject.metaDataProperty.GeocoderMetaData.text,
        coordinates: {
          lng: point[0],
          lat: point[1]
        },
        postalCode: addressData?.postal_code,
        region: addressData?.region,
        city: cityFromComponents ?? addressData?.locality ?? '',
        street: addressData?.street,
        house: addressData?.house
      };
    } catch (error) {
      console.error('Ошибка получения деталей адреса:', error);
      return null;
    }
  }
}

// DaData API сервис (для более точных результатов)
export class DaDataAddressService implements AddressService {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  async searchAddresses(query: string): Promise<AddressSuggestion[]> {
    if (!query || query.length < 3) return [];

    try {
      const response = await fetch('https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${this.apiKey}`
        },
        body: JSON.stringify({
          query: query,
          count: 10,
          locations: [
            { country: 'Россия' }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Ошибка запроса к DaData API');
      }

      const data = await response.json();
      return data.suggestions.map((suggestion: any) => ({
        value: suggestion.value,
        label: suggestion.value,
        coordinates: suggestion.data.geo_lat && suggestion.data.geo_lon ? {
          lat: parseFloat(suggestion.data.geo_lat),
          lng: parseFloat(suggestion.data.geo_lon)
        } : undefined,
        postalCode: suggestion.data.postal_code,
        region: suggestion.data.region,
        city: suggestion.data.city,
        street: suggestion.data.street,
        house: suggestion.data.house
      }));
    } catch (error) {
      console.error('Ошибка поиска адресов в DaData:', error);
      return [];
    }
  }

  async getAddressDetails(address: string): Promise<AddressSuggestion | null> {
    try {
      const response = await fetch('https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/address', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${this.apiKey}`
        },
        body: JSON.stringify({
          query: address
        })
      });

      if (!response.ok) {
        throw new Error('Ошибка запроса к DaData API');
      }

      const data = await response.json();
      const suggestion = data.suggestions[0];

      if (!suggestion) return null;

      return {
        value: suggestion.value,
        label: suggestion.value,
        coordinates: suggestion.data.geo_lat && suggestion.data.geo_lon ? {
          lat: parseFloat(suggestion.data.geo_lat),
          lng: parseFloat(suggestion.data.geo_lon)
        } : undefined,
        postalCode: suggestion.data.postal_code,
        region: suggestion.data.region,
        city: suggestion.data.city,
        street: suggestion.data.street,
        house: suggestion.data.house
      };
    } catch (error) {
      console.error('Ошибка получения деталей адреса в DaData:', error);
      return null;
    }
  }
}

// Backend прокси сервис (использует backend endpoint)
export class BackendAddressService implements AddressService {
  private apiBaseUrl: string;

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl;
  }

  async searchAddresses(query: string): Promise<AddressSuggestion[]> {
    if (!query || query.length < 3) return [];

    try {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const url = `${this.apiBaseUrl}/address/search?query=${encodeURIComponent(query)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`Backend address API error: ${response.status} ${response.statusText}`);
        return [];
      }

      const suggestions = await response.json();
      if (!Array.isArray(suggestions)) return [];
      return suggestions.map((s: Record<string, unknown>) => {
        const shortAddr = String(s.address ?? s.value ?? '');
        const fullAddr =
          typeof s.fullAddress === 'string' && s.fullAddress.trim()
            ? String(s.fullAddress)
            : shortAddr;
        const city = typeof s.city === 'string' ? s.city : '';
        return {
          ...s,
          address: shortAddr,
          fullAddress: fullAddr,
          value: String(s.value ?? shortAddr),
          label: s.label ?? shortAddr,
          city
        } as AddressSuggestion;
      });
    } catch (error) {
      console.error('❌ Ошибка поиска адресов через backend:', error);
      return [];
    }
  }

  async getAddressDetails(addressQuery: string): Promise<AddressSuggestion | null> {
    try {
      const results = await this.searchAddresses(addressQuery);
      return results.length > 0 ? results[0] : null;
    } catch (error) {
      console.error('Ошибка получения деталей адреса:', error);
      return null;
    }
  }
}

// Локальный сервис с базовыми адресами (fallback)
export class LocalAddressService implements AddressService {
  private addresses: AddressSuggestion[] = [
    { value: 'г. Москва, ул. Тверская, д. 1', label: 'г. Москва, ул. Тверская, д. 1' },
    { value: 'г. Санкт-Петербург, Невский пр., д. 1', label: 'г. Санкт-Петербург, Невский пр., д. 1' },
    { value: 'г. Екатеринбург, ул. Ленина, д. 1', label: 'г. Екатеринбург, ул. Ленина, д. 1' },
    { value: 'г. Новосибирск, ул. Красный проспект, д. 1', label: 'г. Новосибирск, ул. Красный проспект, д. 1' },
    { value: 'г. Казань, ул. Баумана, д. 1', label: 'г. Казань, ул. Баумана, д. 1' }
  ];

  async searchAddresses(query: string): Promise<AddressSuggestion[]> {
    if (!query || query.length < 2) return [];

    const lowerQuery = query.toLowerCase();
    return this.addresses.filter(addr => 
      addr.value.toLowerCase().includes(lowerQuery)
    );
  }

  async getAddressDetails(address: string): Promise<AddressSuggestion | null> {
    const found = this.addresses.find(addr => addr.value === address);
    return found || null;
  }
}

// Фабрика для создания сервисов
export class AddressServiceFactory {
  static createYandexService(apiKey: string): AddressService {
    return new YandexAddressService(apiKey);
  }

  static createDaDataService(apiKey: string): AddressService {
    return new DaDataAddressService(apiKey);
  }

  static createLocalService(): AddressService {
    return new LocalAddressService();
  }

  // Создает сервис на основе доступных API ключей
  static createDefaultService(): AddressService {
    const apiBaseUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:3001/api`;
    const normalizedUrl = apiBaseUrl.replace(/\/+$/, '');
    const backendUrl = normalizedUrl.endsWith('/api') ? normalizedUrl : `${normalizedUrl}/api`;
    const yandexKey = process.env.REACT_APP_YANDEX_API_KEY;
    const dadataKey = process.env.REACT_APP_DADATA_API_KEY;

    console.log('🏭 Создание сервиса адресов:');
    console.log('  - Backend URL:', backendUrl);
    console.log('  - Яндекс.Карты API ключ (frontend):', yandexKey ? 'Найден' : 'НЕ НАЙДЕН');
    console.log('  - DaData API ключ:', dadataKey ? 'Найден' : 'НЕ НАЙДЕН');

    // Приоритет: Backend (решает CORS) > DaData > Яндекс.Карты (прямой доступ) > Локальный
    // Backend всегда используется, если доступен (решает проблемы с CORS)
    console.log('  - Выбран сервис: Backend (прокси через API)');
    return new BackendAddressService(backendUrl);
    
    // Fallback на прямые API (если backend недоступен, можно раскомментировать):
    // if (dadataKey) {
    //   console.log('  - Выбран сервис: DaData (fallback)');
    //   return new DaDataAddressService(dadataKey);
    // } else if (yandexKey) {
    //   console.log('  - Выбран сервис: Яндекс.Карты (fallback)');
    //   return new YandexAddressService(yandexKey);
    // } else {
    //   console.log('  - Выбран сервис: Локальный (fallback)');
    //   return new LocalAddressService();
    // }
  }
} 